// Vista de tarjetas para tablas anchas en el celular.
//
// En vez de reescribir cada fila como tarjeta (y duplicar sus botones,
// permisos y handlers), la tabla se sigue renderizando igual y en el celular
// cada fila se muestra como tarjeta por CSS (ver `.axon-stacked` en
// GlobalStyles). Esta utilidad anota las celdas con lo que el CSS necesita:
// la etiqueta de su columna, el orden dentro de la tarjeta y qué celdas quedan
// en el detalle colapsado.

// Flechas de orden que algunas tablas agregan al texto del encabezado.
const SORT_MARKS = /[🔼🔽▲▼↑↓⬆⬇]/gu;
const INTERACTIVE = 'button, select, input, textarea, a, svg, img, [role="button"]';

export const normalizeLabel = (text) =>
  String(text || '').replace(SORT_MARKS, '').replace(/\s+/g, ' ').trim();

// Comparación tolerante a mayúsculas y tildes ("Operacion" == "OPERACIÓN").
const labelKey = (text) =>
  normalizeLabel(text).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const getHeaderLabels = (table) => {
  const headRows = table.tHead ? table.tHead.rows : [];
  if (!headRows.length) return [];
  const labels = [];
  Array.from(headRows[headRows.length - 1].cells).forEach((th) => {
    const label = normalizeLabel(th.textContent);
    for (let i = 0; i < (th.colSpan || 1); i += 1) labels.push(label);
  });
  return labels;
};

const ENABLED_CONTROL = 'button:not(:disabled), select:not(:disabled), input:not(:disabled), textarea:not(:disabled), a, [role="button"]';

// Un guion es "sin dato" en las planillas: en la tarjeta no aporta.
const EMPTY_TEXT = new Set(['', '-', '—', '–']);

const isEmptyCell = (td) => EMPTY_TEXT.has(td.textContent.trim()) && !td.querySelector(INTERACTIVE);

// La columna de botones queda vacía si ninguno está habilitado para el usuario
// (en la tarjeta los deshabilitados no se muestran).
const isEmptyActionsCell = (td) => !td.querySelector(ENABLED_CONTROL);

const setFlag = (el, name, on) => {
  if (on) el.setAttribute(name, '');
  else el.removeAttribute(name);
};

const annotateRow = (tr, labels, config) => {
  const { primaryKeys, titleKey, actionsKey, collapsible } = config;
  const cells = Array.from(tr.cells);
  const isSection = cells.length === 1 && cells[0].colSpan > 1;
  setFlag(tr, 'data-stack-section', isSection);

  let column = 0;
  let hasSecondary = false;
  cells.forEach((td) => {
    // Una celda que abarca varias columnas (totales, títulos de grupo) no es
    // ninguna de ellas: va sin etiqueta.
    const label = td.colSpan > 1 ? '' : (labels[column] || '');
    column += td.colSpan || 1;
    const key = labelKey(label);
    const order = primaryKeys.indexOf(key);
    const isTitle = !!titleKey && key === titleKey;
    const isActions = !!actionsKey && key === actionsKey;
    // Totales y títulos de grupo (colSpan) nunca quedan escondidos en el detalle.
    const isSecondary = collapsible && td.colSpan <= 1 && !isTitle && !isActions && order === -1;

    td.setAttribute('data-label', label);
    setFlag(td, 'data-stack-title', isTitle);
    setFlag(td, 'data-stack-actions', isActions);
    setFlag(td, 'data-stack-secondary', isSecondary);
    setFlag(td, 'data-stack-empty', isActions ? isEmptyActionsCell(td) : isEmptyCell(td));
    if (order >= 0 && !isTitle) td.setAttribute('data-stack-order', String(Math.min(order, 19)));
    else td.removeAttribute('data-stack-order');

    if (isSecondary && !isEmptyCell(td)) hasSecondary = true;
  });
  setFlag(tr, 'data-stack-collapsible', hasSecondary);
};

/**
 * Anota todas las filas de cuerpo y pie de la tabla. Es idempotente: se vuelve
 * a llamar cada vez que cambian las filas.
 *
 * config:
 * - primary: etiquetas de columna visibles en la tarjeta colapsada, en el
 *   orden en que se muestran. Sin `primary` se ven todas las celdas.
 * - title: columna que encabeza la tarjeta (en negrita y sin etiqueta).
 * - actions: columna de botones, al pie de la tarjeta y con áreas táctiles
 *   grandes.
 */
export const annotateStackedTable = (table, { primary = [], title, actions } = {}) => {
  if (!table) return;
  const labels = getHeaderLabels(table);
  const config = {
    primaryKeys: primary.map(labelKey),
    titleKey: title ? labelKey(title) : null,
    actionsKey: actions ? labelKey(actions) : null,
    collapsible: primary.length > 0,
  };
  const bodies = [...Array.from(table.tBodies), ...(table.tFoot ? [table.tFoot] : [])];
  bodies.forEach((body) => Array.from(body.rows).forEach((tr) => annotateRow(tr, labels, config)));
};

/**
 * Si la tabla está dentro de un contenedor de scroll horizontal (la caja
 * blanca con sombra de las planillas), lo marca para que en el celular deje de
 * scrollear y no encierre a las tarjetas en otra caja. Un panel que contiene la
 * tabla junto con otras cosas no se toca.
 */
export const markStackedTableWrapper = (table) => {
  const parent = table && table.parentElement;
  if (!parent || parent.hasAttribute('data-stack-wrap')) return;
  const { overflowX } = window.getComputedStyle(parent);
  if (overflowX === 'auto' || overflowX === 'scroll') parent.setAttribute('data-stack-wrap', '');
};

/**
 * Click en el pie de la tarjeta ("Ver detalle"): el pseudo-elemento ::after de
 * la fila no es un nodo propio, así que el evento llega con la fila como
 * target. Tocar el contenido de una celda nunca expande ni colapsa.
 */
export const toggleStackedRowFromEvent = (event) => {
  const tr = event.target;
  if (!tr || tr.tagName !== 'TR' || !tr.hasAttribute('data-stack-collapsible')) return false;
  setFlag(tr, 'data-stack-expanded', !tr.hasAttribute('data-stack-expanded'));
  return true;
};
